import React from 'react';
import { Link } from 'react-router';
import { Button } from './Button';
import { Modal } from './Modal';
import { TERMS_RESCHEDULE_LOCK_COPY } from '../../shared/legalPolicyCopy';

interface TermsOfServiceModalProps {
  open: boolean;
  onClose: () => void;
}

interface TermsSectionProps {
  title: string;
  children: React.ReactNode;
}

interface TermsSubsectionProps {
  title: string;
  children: React.ReactNode;
}

function TermsSection({ title, children }: TermsSectionProps) {
  return (
    <div className="flex flex-col gap-4 mb-8 round-md bg-white p-4 shadow-md max-w-[800px] mx-auto">
      <h2 className="text-xl font-semibold mb-1">{ title }</h2>
      { children }
    </div>
  );
}

function TermsSubsection({ title, children }: TermsSubsectionProps) {
  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold mb-0">{ title }</h3>
      { children }
    </div>
  );
}

export function TermsOfServiceContent() {
  return (
    <div className="max-w-none">
      <div className="flex flex-col gap-4 mb-8 round-md bg-white p-4 shadow-md max-w-[800px] mx-auto">
        <h2 className="text-xl font-semibold mb-1">Relationship to Privacy Policy</h2>
        <p>
          Our Privacy Policy is dependent on and must be considered in the context of these Terms of Service. Any use of our services, and any interpretation of our privacy practices, is subject to the terms, limitations, and definitions set forth in this document. Please review both documents together for a complete understanding of your rights and obligations.
        </p>
      </div>

      <TermsSection title="Overview">
        <p>
          This website is operated by Better Local Company, LLC doing business as (DBA) "Bidet New York". Throughout the site, the terms “we”, “us” and “our” refer to Better Local Company, LLC. Better Local Company, LLC offers this website, including all information, tools and services available from this site to you, the user, conditioned upon your acceptance of all terms, conditions, policies and notices stated here.
        </p>
        <p>
          By visiting our site and/or purchasing something from us, you engage in our “Service” and agree to be bound by the following terms and conditions (“Terms of Service”, “Terms”). These Terms of Service apply to all users of the site. Please read these Terms of Service carefully before accessing or using our website. By accessing or using any part of the site, you agree to be bound by these Terms of Service.
        </p>
        <p>
          You can review the most current version of the Terms of Service at any time on this page. We reserve the right to update, change or replace any part of these Terms of Service by posting updates and/or changes to our website. It is your responsibility to check this page periodically for changes. Your continued use of or access to the website following the posting of any changes constitutes acceptance of those changes.
        </p>
        <p>
          The headings used in this agreement are included for convenience only and will not limit or otherwise affect these Terms.
        </p>
      </TermsSection>

      <TermsSection title="Section 1: Disclaimer">
        <p>
          The language in these Terms generally favor Bidet New York in the event of a dispute, however it is the deepest desire of Bidet New York to arrive at the outcomes that best serve our Customers, always. We aim to provide excellent customer service and aspire to resolve any issues amicably. We are a small business and we care about our Customers. We want to be your first choice for bidet products and services, and we will do everything we can to earn your trust and respect.
        </p>
      </TermsSection>

      <TermsSection title="Section 2: General Conditions">
        <p>
          We reserve the right to refuse service to anyone for any reason at any time. You understand that your content (not including credit card information), may be transferred unencrypted and involve (a) transmissions over various networks; and (b) changes to conform and adapt to technical requirements of connecting networks or devices. Credit card information is always encrypted during transfer over networks and is handled by our payment processor, Stripe.
        </p>
        <p>
          You agree not to reproduce, duplicate, copy, sell, resell, or exploit any portion of the Service, use of the Service, or access to the Service or any contact on the website through which the service is provided, without express written permission by us.
        </p>
      </TermsSection>

      <TermsSection title="Section 3: Terms of Sale">
        <p>
          By placing an order, you are agreeing to be charged for the order total at the time of ordering.
        </p>
        <TermsSubsection title="Pass-Through Sales Tax for Products">
          <p>
            At this time, Bidet New York is not a reseller of products. As such, when you order a particular bidet or other physical product on our website, we are directly purchasing that item at the available retail price, plus sales tax, on your behalf in a "passthrough" manner. In order to stay legally compliant, we must charge the exact amount that we pay, so we have a policy for how we deal with overcharges and undercharges.
          </p>
          <p>
            If we UNDERCHARGE you, we supplement the amount undercharged from the installation fee, as long as the difference is $10 or less. In other words, we help pay for your product. We offer this as a convenience to avoid delays or disruptive complications in our primary installation service. However, if the amount we undercharged is greater than $10 dollars, we cannot cover the difference and will attempt to reach out to you to cover the full discrepancy and if we are unable to do so, must cancel the order. This is why it's important to provide us with an active phone number and email address when you order.
          </p>
          <p>
            If we OVERCHARGE you for a particular product, we will refund the discrepancy to the payment method you used to make the purchase. Please note that we take great efforts to keep pricing at parity with our main suppliers in order to avoid these adjustments.
          </p>
        </TermsSubsection>

        <TermsSubsection title="Cancellations of Orders">
          <p>
            Orders may be cancelled at any time by either us or our customers. The amount of the order cost we are able to refund depends on the product(s) that were ordered, the scheduled installation time, and the timing of the cancellation.
          </p>
          <p>
            {TERMS_RESCHEDULE_LOCK_COPY} We don't do this to be spiteful, but because we have limited booking times and ordering windows, which if not densely and productively occupied sacrifices the financial security of our business and our ability to pay our employees.
          </p>
          <p>
            Regardless of cancellation time, we are happy to return products that were ordered through us and refund the full amount paid for that product by our customer to our customers, as long as we can recover the full amount. If we can only get back a partial amount or there is a fee to return, then we can only refund the amount we were able to recover minus any fees. And, if we are unable to return the product(s) ordered, then we cannot refund anything. In other words, we can only refund what we're able to recover. In the event we cannot return an item, you are still entitled to that item, as you paid for it, regardless of the cancelled installation. In this case, we can ship or deliver the item to you for a fee, or in some cases free of charge. We will notify you if this is the case and attempt to arrange this. If we're unable to successfully arrange for a shipment or delivery within 14 days of our notification to you, then your items are forfeited to us. Again, we don't do this to be spiteful, we just can't indefinitely store products and have limited storage space (this is New York City, after all!).
          </p>
        </TermsSubsection>

        <TermsSubsection title="Required Compatibility">
          <p>
            We require that the products you want installed are compatible with your toilets, bathrooms, and plumbing. We require that our customers check for and ensure compatibility before placing an order. We do not offer plumbing, electrical, or carpentry services and will not modify your toilets, bathrooms, bidet, plumbing, or electrical systems to make them compatible. If we attempt to install a product and find it to be incompatible, we are happy to try to return it and get a better item and install that, but it is the customer's responsibility to pay the costs associated with returns, new items, and another installation fee. If the customer doesn't wish to do that, or otherwise we can't manage to solve the problem, we will treat it as a cancellation. If you're unsure how to check for compatibility, you may contact us to ask questions, but nothing we tell you transfers the responsibility or inability for ensuring compatibility from you to us.
          </p>
        </TermsSubsection>

        <TermsSubsection title="Payment Processing Fees">
          <p>
            Payment processing fees are included in our costs, are not itemized on invoices, and are typically not of concern to our customers. However, in the event of a refund, depending on the nature of the refund, we must retain a portion of the initial payment to cover the processing fee.
          </p>
          <p>
            We use
            {' '}
            <a target="_blank" href="https://stripe.com/" rel="noreferrer">Stripe</a>
            {' '}
            to process payments. The payment processing fee for Stripe is 2.9% + $0.30.
          </p>
          <p>
            We reserve the right to issue a full refund, including the processing fee, and even exceeding the initial order charge, if we deem it necessary and/or appropriate. This does not otherwise invalidate these Terms or obligate us to do so in future cases.
          </p>
        </TermsSubsection>
      </TermsSection>

      <TermsSection title="Section 4: Health Conditions and Product Suitability">
        <p>
          It is your responsibility to make sure a bidet is right for you. We think they're wonderful, but they are also invasive and in some cases quite forceful. They can be gentle too, but you must purchase the right model and use it the right way for that to be the case. If you have injuries, sores, cuts, disabilities, sensitivities, or anything else that may be unique to you, a bidet may not be the right choice for you. Then again, those conditions could make you a perfect candidate for a bidet. Either way, We make NO REPRESENTATIONS about the suitability of bidets for you.
        </p>
        <p>
          One additional note about bidets and health is that bidets require a clean water line to your toilet, and if your water line isn't clean then the use of a bidet could have adverse side effects. This doesn't only concern bacteria or microbes, but also minerals, chlorine, and other chemicals used to treat water or encountered in building pipes, which could have unintended side effects when applied to your sensitive nether regions. If you want to be safe, get your water tested before purchasing and using a bidet, and if you want to stay safe, we encourage installing a water filter on the line that feeds your bidet.
        </p>
        <p>
          Please note that even if you follow all of our advice, other complications or side effects may arise from the use of a bidet. We are not responsible for any side effects, complications, or injuries that may arise from the use of a bidet. We are not doctors and do not offer medical advice. If you have any questions or concerns about the use of a bidet, please consult with your doctor or healthcare provider.
        </p>
      </TermsSection>

      <TermsSection title="Section 5: Limitation of Liability">
        <p>
          While we maintains comprehensive Insurance to cover our services, you agree to use our services at your own risk. We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including, but not limited to, damages for loss of profits, goodwill, use, data, or other intangible losses resulting from:
        </p>
        <ol>
          <li>
            <p>
              your access to or use of or inability to access or use the services;
            </p>
          </li>
          <li>
            <p>
              any conduct or content of any third party on the services;
            </p>
          </li>
          <li>
            <p>
              any content obtained from the services; and
            </p>
          </li>
          <li>
            <p>
              unauthorized access, use, or alteration of your transmissions or content,
            </p>
          </li>
        </ol>
        <p>
          whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not the company has been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose.
        </p>
      </TermsSection>

      <TermsSection title="Section 6: Accuracy, Completeness, and Timeliness Of Information">
        <p>
          We are not responsible if information made available on our site or during our installations is not accurate, complete or current. We provide information for general information only and we should not be relied upon or used as the sole basis for making decisions without consulting primary, more accurate, more complete, or more timely sources of information. Any reliance on the material on this site is at your own risk.
        </p>
        <p>
          This site may contain certain historical information. Historical information is provided for your reference only. We reserve the right to modify the contents of this site at any time, but we have no obligation to update any information on our site. You agree that it is your responsibility to monitor changes to our site.
        </p>
      </TermsSection>

      <TermsSection title="Section 7: Third-Party Links">
        <p>
          Certain content, products and services available via our Service may include materials from third-parties. Third-party links on this site may direct you to third-party websites that are not affiliated with us. We are not responsible for examining or evaluating the content or accuracy and we do not warrant and will not have any liability or responsibility for any third-party materials or websites, or for any other materials, products, or services of third-parties.
        </p>
        <p>
          We are not liable for any harm or damages related to the purchase or use of goods, services, resources, content, or any other transactions made in connection with any third-party websites. Please review carefully the third-party's policies and practices and make sure you understand them before you engage in any transaction. Complaints, claims, concerns, or questions regarding third-party products should be directed to the third-party.
        </p>
      </TermsSection>

      <TermsSection title="Section 8: User Comments, Feedback and Other Submissions">
        <p>
          If, at our request, you send certain specific submissions (for example contest entries) or without a request from us you send creative ideas, suggestions, proposals, plans, or other materials, whether online, by email, by postal mail, or otherwise (collectively, 'comments'), you agree that we may, at any time, without restriction, edit, copy, publish, distribute, translate and otherwise use in any medium any comments that you forward to us. We are and shall be under no obligation (1) to maintain any comments in confidence; (2) to pay compensation for any comments; or (3) to respond to any comments.
        </p>
        <p>
          We may, but have no obligation to, monitor, edit or remove content that we determine in our sole discretion are unlawful, offensive, threatening, libelous, defamatory, pornographic, obscene or otherwise objectionable or violates any party’s intellectual property or these Terms of Service.
        </p>
        <p>
          You agree that your comments will not violate any right of any third-party, including copyright, trademark, privacy, personality or other personal or proprietary right. You further agree that your comments will not contain libelous or otherwise unlawful, abusive or obscene material, or contain any computer virus or other malware that could in any way affect the operation of the Service or any related website. You may not use a false e-mail address, pretend to be someone other than yourself, or otherwise mislead us or third-parties as to the origin of any comments. You are solely responsible for any comments you make and their accuracy. We take no responsibility and assume no liability for any comments posted by you or any third-party.
        </p>
      </TermsSection>

      <TermsSection title="Section 9: Personal Information">
        <p>
          Your submission of personal information through the store is governed by our Privacy Policy. To view our Privacy Policy, please visit the link at the bottom of this page.
        </p>
      </TermsSection>

      <TermsSection title="Section 10: Optional Tools">
        <p>
          We may provide you with access to third-party tools that we neither monitor nor control the input of. You acknowledge and agree that we provide access to such tools 'as is' and 'as available' without any warranties, representations or conditions of any kind and without any endorsement. We shall have no liability whatsoever arising from or relating to your use of optional third-party tools.
        </p>
        <p>
          Any use by you of optional tools offered through the site is entirely at your own risk and discretion and you should ensure that you are familiar with and approve of the terms on which tools are provided by the relevant third-party provider(s).
        </p>
      </TermsSection>

      <TermsSection title="Section 11: Accuracy of Billing and Account Information">
        <p>
          You agree to provide current, complete and accurate purchase and account information for all purchases made at our store. You agree to promptly update your account and other information, including your email address and credit card numbers and expiration dates, so that we can complete your transactions and contact you as needed.
        </p>
      </TermsSection>

      <TermsSection title="Section 12: Errors, Inaccuracies and Omissions">
        <p>
          There may be information on our site or in the Service that contains typographical errors, inaccuracies or omissions that may relate to product descriptions, pricing, promotions, offers, product shipping charges, transit times, warranties, and availability. We reserve the right to correct any errors, inaccuracies or omissions, and to change or update information or cancel orders if any information in the Service or on any related website is inaccurate at any time without prior notice (including after you have submitted your order).
        </p>
        <p>
          We undertake no obligation to update, amend or clarify information in the Service or on any related website, including without limitation, pricing information, except as required by law. No specified update or refresh date applied in the Service or on any related website, should be taken to indicate that all information in the Service or on any related website has been modified or updated.
        </p>
      </TermsSection>

      <TermsSection title="Section 13: Prohibited Uses">
        <p>
          In addition to other prohibitions as set forth in the Terms of Service, you are prohibited from using the site or its content:
        </p>
        <ul className="list-disc pl-6">
          <li>
            <p>
              for any unlawful purpose;
            </p>
          </li>
          <li>
            <p>
              to solicit others to perform or participate in any unlawful acts;
            </p>
          </li>
          <li>
            <p>
              to violate any international, federal, provincial or state regulations, rules, laws, or local ordinances;
            </p>
          </li>
          <li>
            <p>
              to infringe upon or violate our intellectual property rights or the intellectual property rights of others;
            </p>
          </li>
          <li>
            <p>
              to harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate based on gender, sexual orientation, religion, ethnicity, race, age, national origin, or disability;
            </p>
          </li>
          <li>
            <p>
              to submit false or misleading information;
            </p>
          </li>
          <li>
            <p>
              to upload or transmit viruses or any other type of malicious code that will or may be used in any way that will affect the functionality or operation of the Service or of any related website, other websites, or the Internet;
            </p>
          </li>
          <li>
            <p>
              to collect or track the personal information of others;
            </p>
          </li>
          <li>
            <p>
              to spam, phish, pharm, pretext, spider, crawl, or scrape;
            </p>
          </li>
          <li>
            <p>
              for any obscene or immoral purpose; or
            </p>
          </li>
          <li>
            <p>
              to interfere with or circumvent the security features of the Service or any related website, other websites, or the Internet;
            </p>
          </li>
          <li>
            <p>
              to perform penetration tests or scans of our digital assets without our prior written consent.
            </p>
          </li>
        </ul>
        <p>
          Such unauthorized activities are a breach of our website’s terms and of use. We reserve the right to terminate your use of the Service or any related website for violating any of the prohibited uses.
        </p>
      </TermsSection>
      <TermsSection title="Section 14: Disclaimer Of Warranties; Limitation Of Liability">
        <p>
          We do not guarantee, represent or warrant that your use of our service will be uninterrupted, timely, secure or error-free.
        </p>
        <p>
          We do not warrant that the results that may be obtained from the use of the service will be accurate or reliable.
        </p>
        <p>
          You agree that from time to time we may remove the service for indefinite periods of time or cancel the service at any time, without notice to you.
        </p>
        <p>
          You expressly agree that your use of, or inability to use, the service is at your sole risk. The service and all products and services delivered to you through the service are (except as expressly stated by us) provided 'as is' and 'as available' for your use, without any representation, warranties or conditions of any kind, either express or implied, including all implied warranties or conditions of merchantability, merchantable quality, fitness for a particular purpose, durability, title, and non-infringement.
        </p>
        <p>
          In no case shall Better Local Company, Inc., our directors, officers, employees, affiliates, agents, contractors, interns, suppliers, service providers or licensors be liable for any injury, loss, claim, or any direct, indirect, incidental, punitive, special, or consequential damages of any kind, including, without limitation lost profits, lost revenue, lost savings, loss of data, replacement costs, or any similar damages, whether based in contract, tort (including negligence), strict liability or otherwise, arising from your use of any of the service or any products procured using the service, or for any other claim related in any way to your use of the service or any product, including, but not limited to, any errors or omissions in any content, or any loss or damage of any kind incurred as a result of the use of the service or any content (or product) posted, transmitted, or otherwise made available via the service, even if advised of their possibility. Because some states or jurisdictions do not allow the exclusion or the limitation of liability for consequential or incidental damages, in such states or jurisdictions, our liability shall be limited to the maximum extent permitted by law.
        </p>
      </TermsSection>

      <TermsSection title="Section 15: Indemnification">
        <p>
          You agree to indemnify, defend and hold harmless Better Local Company, LLC. and our parent, subsidiaries, affiliates, partners, officers, directors, agents, contractors, licensors, service providers, subcontractors, suppliers, interns, and employees harmless from any claim or demand, including reasonable attorneys’ fees, made by any third-party due to or arising out of your breach of these Terms of Service or the documents they incorporate by reference, or your violation of any law or the rights of a third-party.
        </p>
      </TermsSection>

      <TermsSection title="Section 16: Severability">
        <p>
          In the event that any provision of these Terms of Service is determined to be unlawful, void or unenforceable, such provision shall nonetheless be enforceable to the fullest extent permitted by applicable law, and the unenforceable portion shall be deemed to be severed from these Terms of Service, such determination shall not affect the validity and enforceability of any other remaining provisions.
        </p>
      </TermsSection>

      <TermsSection title="Section 17: Termination">
        <p>
          The obligations and liabilities of the parties incurred prior to the termination date shall survive the termination of this agreement for all purposes.
        </p>
        <p>
          These Terms of Service are effective unless and until terminated by either you or us. You may terminate these Terms of Service at any time by notifying us that you no longer wish to use our Services and then waiting to receive a confirmation message from us.
        </p>
        <p>
          If in our sole judgment you fail, or we suspect that you have failed, to comply with any term or provision of these Terms of Service, we also may terminate this agreement at any time without notice and you will remain liable for all amounts due up to and including the date of termination; and/or accordingly may deny you access to our Services (or any part thereof).
        </p>
      </TermsSection>

      <TermsSection title="Section 18: Entire Agreement">
        <p>
          The failure of us to exercise or enforce any right or provision of these Terms of Service shall not constitute a waiver of such right or provision.
        </p>
        <p>
          These Terms of Service and any policies or operating rules posted by us on this site or in respect to The Service constitutes the entire agreement and understanding between you and us and govern your use of the Service, superseding any prior or contemporaneous agreements, communications and proposals, whether oral or written, between you and us (including, but not limited to, any prior versions of the Terms of Service).
        </p>
        <p>
          Any ambiguities in the interpretation of these Terms of Service shall not be construed against the drafting party.
        </p>
      </TermsSection>

      <TermsSection title="Section 19: Governing Law">
        <p>
          These Terms of Service and any separate agreements whereby we provide you Services shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles.
        </p>
      </TermsSection>

      <TermsSection title="Section 20: Changes to Terms Of Service">
        <p>
          You can review the most current version of the Terms of Service at any time at this page.
        </p>
        <p>
          We reserve the right, at our sole discretion, to update, change or replace any part of these Terms of Service by posting updates and changes to our website. It is your responsibility to check our website periodically for changes. Your continued use of or access to our website or the Service following the posting of any changes to these Terms of Service constitutes acceptance of those changes.
        </p>
        <p>
          When using our website, our services, and interacting with us, your use and disputes regarding privacy are subject to these Terms of Service and our
          {' '}
          <Link to="/privacy">Privacy Policy</Link>
          , including the application of the law in the state of New York and limitations on damages and losses.
        </p>
        <p>
          If you have any concern about privacy, please
          <a href="mailto:support@bidetny.com">contact us</a>
          {' '}
          and we will aim to resolve it.
        </p>
      </TermsSection>

      <TermsSection title="Section 21: Minimum Age">
        <p>
          By using this site, you represent that you are at least the age of majority in your state or province of residence, or that you are the age of majority in your state or province of residence and you have given us your consent to allow any of your minor dependents to use this site.
        </p>
      </TermsSection>
    </div>
  );
}

export const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ open, onClose }) => (
  <Modal
    open={open}
    onClose={onClose}
    overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
    containerClassName="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-lg"
    ariaLabel="Terms of Service Modal"
    showX
  >
    <div className="w-full text-black relative text-left p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Terms of Service</h1>
      <TermsOfServiceContent />
    </div>
  </Modal>
);
